import React, { useEffect, useMemo } from 'react';
import RadarChart from '../../components/RadarChart';
import { HairSalonBrandLogo } from '../../utils/hairSalonFlowDeps';
import { resolveInsightAssetUrl } from '../../utils/insightApi';
import { resolveHairReportCustomer } from '../../utils/reportCustomer';

function ScoreRing({ score, size = 88, tone = 'fair', label }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const stroke =
    tone === 'good' ? '#16a34a' : tone === 'moderate' ? '#f59e0b' : tone === 'low' ? '#dc2626' : '#eab308';
  return (
    <div className="hi-report__ring" style={{ width: size, height: size }} role="img" aria-label={label || `${pct} of 100`}>
      <svg viewBox="0 0 88 88">
        <circle cx="44" cy="44" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="44"
          cy="44"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 44 44)"
        />
      </svg>
      <span className="hi-report__ring-val">
        {score != null && score !== '' ? score : '—'}
        <small>/100</small>
      </span>
    </div>
  );
}

export function obsText(obs) {
  if (obs == null || obs === '') return '—';
  if (typeof obs === 'string' || typeof obs === 'number') {
    return String(obs).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  const v = String(obs.value || obs.display || '—').replace(/_/g, ' ');
  return v.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function statusTone(raw) {
  const s = String(raw || '')
    .toLowerCase()
    .replace(/_/g, ' ');
  if (!s || s === '—') return 'fair';
  if (/(good|none|low|intact|clear|excellent|full|dense)/.test(s)) return 'good';
  if (/(mild|fair|medium|fine|balanced)/.test(s)) return 'mild';
  if (/(moderate|mixed|reduced)/.test(s)) return 'moderate';
  if (/(high|severe|poor|low coverage|thin|sparse|bald)/.test(s)) return 'low';
  return 'fair';
}

function healthMetricScore(val, { invert = false } = {}) {
  const text = obsText(val).toLowerCase();
  if (!text || text === '—' || text.includes('not assess')) return null;
  if (invert) {
    if (/(none|clear|intact)/.test(text) || (/\blow\b/.test(text) && !/yellow/.test(text))) return 88;
    if (/(mild|fine)/.test(text)) return 72;
    if (/(moderate|medium|mixed|fair)/.test(text)) return 52;
    if (/(high|severe|poor)/.test(text)) return 32;
    return 55;
  }
  if (/(excellent|high|good|thick|coarse|none|clear|intact|balanced|full|dense)/.test(text)) return 88;
  if (/(moderate|medium|mixed|fair)/.test(text)) return 62;
  if (/(mild|fine)/.test(text)) return 72;
  if (/(low|poor|thin|limited|severe|sparse|bald)/.test(text)) return 38;
  return 55;
}

function barTone(score) {
  if (score == null) return 'fair';
  if (score >= 72) return 'good';
  if (score >= 55) return 'moderate';
  return 'low';
}

const SCALP_METRIC_KEYS = [
  { key: 'Condition', field: 'scalpCondition' },
  { key: 'Flaking', field: 'flaking', invert: true },
  { key: 'Oil', field: 'oiliness', invert: true },
  { key: 'Buildup', field: 'productBuildup', invert: true },
  { key: 'Follicles', field: 'follicleVisibility' },
];

export function buildViewMetrics(viewFindings = {}) {
  return [
    { key: 'Density', obs: viewFindings.hairDensity || viewFindings.density },
    { key: 'Thickness', obs: viewFindings.hairThickness || viewFindings.thickness },
    { key: 'Shaft', obs: viewFindings.shaftCondition },
    ...SCALP_METRIC_KEYS.map(({ key, field, invert }) => ({
      key,
      field,
      invert,
      obs: viewFindings[field],
    })),
  ]
    .map((row) => ({
      ...row,
      label: obsText(row.obs),
      score: healthMetricScore(row.obs, { invert: Boolean(row.invert) }),
    }))
    .filter((row) => row.score != null);
}

function viewFindingsHasData(viewFindings) {
  return Object.keys(viewFindings || {}).some((k) => {
    const obs = viewFindings[k];
    if (obs == null || typeof obs === 'string') return Boolean(obs) && obs !== 'not_assessable';
    const v = obs?.value || obs?.display;
    return Boolean(v) && v !== 'not_assessable';
  });
}

export function HairInsightReportCharts({ dashboard }) {
  const hair = dashboard?.hairCondition || {};
  const scalp = dashboard?.scalpInsight || {};
  const overall = dashboard?.coverageMap?.overallScore;
  const stage = dashboard?.coverageMap?.coverageStage || dashboard?.coverageMap?.overallDisplay;
  const conf = dashboard?.analysisConfidence?.label || dashboard?.analysisConfidence?.level;

  const bars = useMemo(
    () =>
      [
        { key: 'Density', score: healthMetricScore(hair.density), obs: obsText(hair.density) },
        { key: 'Thickness', score: healthMetricScore(hair.thickness), obs: obsText(hair.thickness) },
        { key: 'Shaft Health', score: healthMetricScore(hair.shaftCondition), obs: obsText(hair.shaftCondition) },
        {
          key: 'Hydration',
          score: healthMetricScore(hair.drynessAppearance, { invert: true }),
          obs: obsText(hair.drynessAppearance),
        },
        {
          key: 'Scalp Clarity',
          score: healthMetricScore(scalp.flaking, { invert: true }),
          obs: obsText(scalp.flaking),
        },
        {
          key: 'Cleanliness',
          score: healthMetricScore(scalp.oilBuildup, { invert: true }),
          obs: obsText(scalp.oilBuildup),
        },
        {
          key: 'Follicles',
          score: healthMetricScore(scalp.follicleVisibility),
          obs: obsText(scalp.follicleVisibility),
        },
        {
          key: 'Breakage',
          score: healthMetricScore(hair.breakageAppearance, { invert: true }),
          obs: obsText(hair.breakageAppearance),
        },
      ].filter((row) => row.score != null),
    [hair, scalp],
  );

  const radarData = useMemo(() => {
    const labels = {
      Density: 'Density',
      Thickness: 'Thickness',
      'Shaft Health': 'Shaft',
      Hydration: 'Hydration',
      'Scalp Clarity': 'Clarity',
      Cleanliness: 'Clean',
      Follicles: 'Follicles',
      Breakage: 'Breakage',
    };
    const data = {};
    bars.forEach((row) => {
      data[labels[row.key] || row.key] = row.score;
    });
    return data;
  }, [bars]);

  return (
    <section className="hi-report__card hi-report__charts hi-report__charts--visual">
      <h2 className="hi-report__section-title">Hair &amp; Scalp Profile</h2>
      {bars.length ? (
        <div className="hi-report__charts-grid">
          <div className="hi-report__chart-panel hi-report__chart-panel--radar">
            <h3>Overview</h3>
            <div className="hi-report__radar-wrap">
              <RadarChart data={radarData} />
            </div>
          </div>
          <div className="hi-report__chart-panel">
            <h3>Scores</h3>
            <div className="hi-report__profile-bars">
              {bars.map((row) => (
                <div key={row.key} className="hi-report__profile-bar">
                  <div className="hi-report__profile-bar-meta">
                    <strong>{row.key}</strong>
                    <span className={`hi-report__pill is-${statusTone(row.obs)}`}>{row.obs}</span>
                    <em>{row.score}%</em>
                  </div>
                  <div className="hi-report__bar-track" aria-hidden>
                    <span
                      className={`hi-report__bar-fill is-${barTone(row.score)}`}
                      style={{ width: `${Math.max(4, row.score)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="hi-report__chart-empty">Profile scores appear after analysis.</p>
      )}
      <div className="hi-report__chart-summary">
        <div>
          <span className="k">Overall</span>
          <span className="v">{overall != null ? `${overall}` : '—'}</span>
        </div>
        <div>
          <span className="k">Coverage look</span>
          <span className="v">{stage || '—'}</span>
        </div>
        <div>
          <span className="k">Confidence</span>
          <span className="v">{conf || '—'}</span>
        </div>
      </div>
    </section>
  );
}

const PIN_COLORS = ['#f59e0b', '#16a34a', '#2563eb', '#7c3aed', '#ea580c', '#0d9488'];

function IconShield({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3l7 3v5c0 5-3.2 8.6-7 10-3.8-1.4-7-5-7-10V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path d="M9.2 12.2l1.9 1.9 3.7-3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconSpark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IconScope() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M16 16l4.2 4.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function CoverageGauge({ score, display }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const r = 70;
  const c = Math.PI * r;
  const offset = c - (pct / 100) * c;
  const tone = statusTone(display);
  const stroke =
    tone === 'good' ? '#16a34a' : tone === 'moderate' ? '#f59e0b' : tone === 'low' ? '#dc2626' : '#eab308';

  return (
    <div className="hi-report__gauge-wrap">
      <div className="hi-report__gauge" role="img" aria-label={`Score ${pct} of 100`}>
        <svg viewBox="0 0 180 110">
          <path
            d="M20 100 A70 70 0 0 1 160 100"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="14"
            strokeLinecap="round"
          />
          <path
            d="M20 100 A70 70 0 0 1 160 100"
            fill="none"
            stroke={stroke}
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={`${c}`}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="hi-report__gauge-center">
          <div className="big">
            {score != null && score !== '' ? score : '—'}
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--skin-text-muted, #6b7280)' }}> / 100</span>
          </div>
        </div>
      </div>
      <div className="hi-report__status">
        <i className={`hi-report__dot is-${tone}`} />
        <span>{display || 'Coverage Appearance'}</span>
      </div>
      <p className="hi-report__gauge-center sub" style={{ position: 'static', transform: 'none' }}>
        Overall Hair Coverage Score
      </p>
    </div>
  );
}

export function HairInsightReportHeader({
  dashboard,
  orgLogoUrl = '',
  showLogo = false,
  onLogoError,
  orgName = '',
}) {
  const overallScore = dashboard?.coverageMap?.overallScore;
  const overallDisplay =
    dashboard?.coverageMap?.coverageStage ||
    dashboard?.coverageMap?.overallDisplay ||
    'Fair';
  const created =
    dashboard?.createdAt ||
    dashboard?.analyzedAt ||
    dashboard?.timestamp ||
    null;
  const when = created
    ? new Date(created).toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : new Date().toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
  const mode = dashboard?.scanMode === 'quick' ? 'Quick scan' : 'Advanced scan';

  const reportTitle = orgName ? `${orgName} Hair Insight` : 'Hair Insight';

  return (
    <header className="hi-report__card hi-report__header hi-report__header--visual">
      <div className="hi-report__brand">
        <div className="hi-report__brand-top">
          {(showLogo || orgName) ? (
            <HairSalonBrandLogo
              showLogo={showLogo}
              orgLogoUrl={orgLogoUrl}
              onLogoError={onLogoError}
              className="hi-report__org-logo"
              imgClassName="hi-report__org-logo-img"
              fallbackClassName="hi-report__org-logo-fallback"
              alt={orgName || 'Salon'}
            />
          ) : null}
          <div className="hi-report__brand-copy">
            <div className="hi-report__title-row">
              <IconShield className="hi-report__shield" />
              <h1>{reportTitle}</h1>
            </div>
            <div className="hi-report__meta">
              <span>{when}</span>
              <span>{mode}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="hi-report__score-card hi-report__score-card--ring">
        <ScoreRing
          score={overallScore}
          size={84}
          tone={barTone(Number(overallScore))}
          label="Overall coverage score"
        />
        <div className="hi-report__score-copy">
          <span className="label">Coverage</span>
          <span className={`hi-report__pill is-${statusTone(overallDisplay)}`}>{overallDisplay}</span>
        </div>
      </div>
    </header>
  );
}

export function HairInsightReportOverview({ dashboard }) {
  const customer = useMemo(() => resolveHairReportCustomer(dashboard), [dashboard]);
  const hair = dashboard?.hairCondition || {};
  const density = obsText(hair.density);
  const thickness = obsText(hair.thickness);
  const shaft = obsText(hair.shaftCondition);
  const stage =
    dashboard?.coverageMap?.coverageStage ||
    dashboard?.coverageMap?.overallDisplay ||
    '—';
  const score = dashboard?.coverageMap?.overallScore;
  const insight = String(dashboard?.customerSummary?.overallInsight || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 280);

  return (
    <section className="hi-report__card hi-report__card--overview hi-report__card--visual hi-report__lead">
      <div className="hi-report__customer-card">
        <div className="hi-report__hero-photo hi-report__hero-photo--customer">
          {customer.photoUrl ? (
            <img src={customer.photoUrl} alt={customer.name} />
          ) : (
            <div className="hi-report__hero-ph">{customer.initial}</div>
          )}
          <span className="hi-report__hero-badge">Customer</span>
        </div>
        <div className="hi-report__customer-details">
          <h2 className="hi-report__customer-name">{customer.name}</h2>
          <dl className="hi-report__customer-meta">
            {customer.mobile ? (
              <>
                <dt>Mobile</dt>
                <dd>{customer.mobile}</dd>
              </>
            ) : null}
            {customer.age ? (
              <>
                <dt>Age</dt>
                <dd>{customer.age}</dd>
              </>
            ) : null}
            {customer.gender ? (
              <>
                <dt>Gender</dt>
                <dd>{customer.gender}</dd>
              </>
            ) : null}
            {customer.customerCode ? (
              <>
                <dt>Visit code</dt>
                <dd>{customer.customerCode}</dd>
              </>
            ) : null}
            {customer.email ? (
              <>
                <dt>Email</dt>
                <dd>{customer.email}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>
      <div className="hi-report__lead-panel">
        <div className="hi-report__lead-head">
          <h2>Coverage look</h2>
          <ScoreRing score={score} size={76} tone={barTone(Number(score))} label="Overall coverage score" />
        </div>
        <p className="hi-report__lead-copy">
          {insight || 'Density, coverage, and scalp show-through from the full hair photo.'}
        </p>
        <div className="hi-report__lead-metrics">
          <div className="hi-report__hero-metric">
            <span className="k">Density</span>
            <span className="v">{density}</span>
          </div>
          <div className="hi-report__hero-metric">
            <span className="k">Thickness</span>
            <span className="v">{thickness}</span>
          </div>
          <div className="hi-report__hero-metric">
            <span className="k">Shaft</span>
            <span className="v">{shaft}</span>
          </div>
          <div className="hi-report__hero-metric">
            <span className="k">Look</span>
            <span className="v">{stage}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function buildHairMicroscopeViews(dashboard) {
  const closeLooks = dashboard?.visualStory?.closeLooks || [];
  if (closeLooks.length) return closeLooks;
  const overlays = dashboard?.overlays || [];
  if (overlays.length) {
    return overlays.map((ov) => ({
      available: true,
      captureId: ov.captureId,
      imageUrl: ov.originalUrl || ov.overlayUrl,
      view: ov.view,
      overlayUrl: ov.overlayUrl,
      annotations: ov.annotations || [],
      legend: ov.legend || [],
    }));
  }
  const caps = (Array.isArray(dashboard?.captures) ? dashboard.captures : []).filter(
    (c) => c?.source === 'microscope' && c?.imageUrl,
  );
  return caps.map((c) => ({
    available: true,
    captureId: c.captureId,
    imageUrl: c.imageUrl,
    view: c.view,
    overlayUrl: null,
    annotations: [],
    legend: [],
  }));
}

export function HairInsightReportMicroscope({
  dashboard,
  selectedAnnotation,
  onSelectAnnotation,
  activeView,
  onActiveView,
}) {
  const overlays = dashboard?.overlays || [];
  const views = buildHairMicroscopeViews(dashboard);

  useEffect(() => {
    if (!views.length) return;
    if (!activeView || !views.some((v) => v.view === activeView)) {
      onActiveView?.(views[0].view);
    }
  }, [activeView, onActiveView, views]);

  const active = views.find((v) => v.view === activeView) || views[0] || null;
  const ov =
    overlays.find((o) => o.captureId === active?.captureId) ||
    overlays.find((o) => o.view === active?.view) ||
    overlays[0];
  const imageUrl = resolveInsightAssetUrl(
    active?.overlayUrl || active?.imageUrl || ov?.overlayUrl || ov?.originalUrl,
  );
  const viewKey = String(active?.view || ov?.view || '').toLowerCase();
  const annotations = (active?.annotations || ov?.annotations || []).filter(
    (a) => a && (a.type !== 'region' || !(a.width > 0.15)),
  );
  const pins = annotations.slice(0, 6).map((ann, idx) => ({
    ...ann,
    n: idx + 1,
    color: ann.color || PIN_COLORS[idx % PIN_COLORS.length],
  }));

  if (!imageUrl) {
    return (
      <section className="hi-report__card">
        <h2 className="hi-report__section-title">
          <IconScope /> Scalp Close-ups
        </h2>
        <p className="hi-report__warn">No scalp close-ups for this session.</p>
      </section>
    );
  }

  const gallery = views
    .map((v) => ({
      ...v,
      src: resolveInsightAssetUrl(v.overlayUrl || v.imageUrl),
    }))
    .filter((v) => v.src);

  return (
    <section className="hi-report__card hi-report__micro-card">
      <div className="hi-report__section-head">
        <h2 className="hi-report__section-title">
          <IconScope /> Scalp Close-ups
        </h2>
      </div>

      {gallery.length > 1 ? (
        <div className="hi-report__gallery" role="tablist" aria-label="Scalp regions">
          {gallery.map((v) => (
            <button
              key={v.captureId || v.view}
              type="button"
              role="tab"
              className={`hi-report__gallery-item ${activeView === v.view ? 'is-active' : ''}`}
              onClick={() => {
                onActiveView?.(v.view);
                onSelectAnnotation?.(null);
              }}
            >
              <img src={v.src} alt="" />
              <span>{String(v.view || 'scalp').replace(/_/g, ' ')}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="hi-report__micro hi-report__micro--visual">
        <div className="hi-report__micro-media">
          <div className="hi-report__micro-frame">
            <img src={imageUrl} alt={`${active?.view || 'Scalp'} close-up`} />
            <span className="hi-report__micro-tag">
              {String(active?.view || 'Scalp').replace(/_/g, ' ')}
            </span>
            <span className="hi-report__micro-zoom">40×</span>
            <div className="hi-report__annot-layer">
              {pins.map((pin) => (
                <button
                  key={pin.id || pin.n}
                  type="button"
                  className={`hi-report__pin ${selectedAnnotation?.id === pin.id ? 'is-active' : ''}`}
                  style={{
                    left: `${(pin.x ?? 0.5) * 100}%`,
                    top: `${(pin.y ?? 0.5) * 100}%`,
                    background: pin.color,
                  }}
                  onClick={() => onSelectAnnotation?.(pin)}
                  aria-label={pin.label || `Pin ${pin.n}`}
                >
                  {pin.n}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="hi-report__micro-side">
          <h3>{String(active?.view || 'Scalp').replace(/_/g, ' ')} close-up</h3>
          <p className="hi-report__zone-hint">
            Tap pins to highlight spots. Per-zone scores for hairline, top, and crown are in the section below.
          </p>
          <div className="hi-report__obs-pills">
            {pins.map((pin) => {
              const status = obsText(pin.status || pin.display || pin.observation || pin.label || 'Noted');
              const tone = statusTone(status);
              return (
                <button
                  key={pin.id || pin.n}
                  type="button"
                  className={`hi-report__obs-pill ${selectedAnnotation?.id === pin.id ? 'is-active' : ''}`}
                  onClick={() => onSelectAnnotation?.(pin)}
                >
                  <i style={{ background: pin.color || PIN_COLORS[(pin.n - 1) % PIN_COLORS.length] }}>{pin.n}</i>
                  <strong>{pin.label || `Spot ${pin.n}`}</strong>
                  <span className={`hi-report__pill is-${tone}`}>{status === '—' ? 'Noted' : status}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function HairInsightReportConditions({ dashboard }) {
  const score = dashboard?.coverageMap?.overallScore;
  const display =
    dashboard?.coverageMap?.coverageStage ||
    dashboard?.coverageMap?.overallDisplay ||
    'Fair';
  const regions = dashboard?.coverageMap?.regions || {};
  const notes = dashboard?.customerSummary?.regionNotes || {};
  const zoneKeys = ['hairline', 'top', 'crown'];

  return (
    <div className="hi-report__row hi-report__row--conditions">
      <section className="hi-report__card hi-report__cond-card">
        <h3>Overall Coverage Score</h3>
        <CoverageGauge score={score} display={display} />
      </section>
      <section className="hi-report__card hi-report__zone-card">
        <h3>Hairline · Top · Crown</h3>
        <p className="hi-report__zone-hint">From the overview photo — not separate captures.</p>
        <div className="hi-report__zone-list">
          {zoneKeys.map((key) => {
            const r = regions[key] || {};
            const note = String(notes[key] || '').trim();
            return (
              <div key={key} className="hi-report__zone-row">
                <div className="hi-report__zone-row-top">
                  <strong>{r.label || key}</strong>
                  <em>{r.score != null ? `${r.score}` : '—'}</em>
                </div>
                <div className="hi-report__bar-track" aria-hidden>
                  <span
                    className={`hi-report__bar-fill is-${barTone(r.score)}`}
                    style={{ width: `${Math.max(4, Number(r.score) || 0)}%` }}
                  />
                </div>
                <div className="hi-report__zone-pills">
                  <span className={`hi-report__pill is-${statusTone(r.display || r.coverage)}`}>
                    {obsText(r.display || r.coverage)}
                  </span>
                  {r.scalpVisibility && r.scalpVisibility !== 'not_assessable' ? (
                    <span className={`hi-report__pill is-${statusTone(r.scalpVisibility)}`}>
                      Scalp {obsText(r.scalpVisibility)}
                    </span>
                  ) : null}
                </div>
                {note ? <p>{note}</p> : null}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function normalizeScalpZoneKey(view) {
  const v = String(view || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!v) return '';
  if (v.includes('hairline') || v.includes('front') || v.includes('forehead')) return 'hairline';
  if (v.includes('crown') || v.includes('occipital') || v.includes('back')) return 'crown';
  if (v.includes('top') || v.includes('vertex') || v.includes('mid') || v.includes('center')) return 'top';
  const first = v.split(/\s+/)[0];
  if (['hairline', 'top', 'crown'].includes(first)) return first;
  return '';
}

export function HairInsightReportScalpDetail({ dashboard, activeView = null }) {
  const isAdvanced = dashboard?.scanMode !== 'quick';
  const viewFindings = dashboard?.viewFindings || {};
  const zoneKeys = ['hairline', 'top', 'crown'];
  const zoneRows = zoneKeys
    .map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      metrics: buildViewMetrics(viewFindings[key] || {}),
    }))
    .filter((row) => row.metrics.length > 0);

  const activeZoneKey = normalizeScalpZoneKey(activeView);
  const resolvedActiveZone =
    (activeZoneKey && zoneRows.some((z) => z.key === activeZoneKey) && activeZoneKey) ||
    zoneRows[0]?.key ||
    '';

  const scalp = dashboard?.scalpInsight || {};
  if (!isAdvanced && !scalp.available && !zoneRows.length) return null;

  const aggregateMeters = zoneRows.length
    ? []
    : [
        { key: 'Condition', obs: scalp.scalpCondition },
        { key: 'Flaking', obs: scalp.flaking, invert: true },
        { key: 'Oil', obs: scalp.oiliness || scalp.oilBuildup, invert: true },
        { key: 'Buildup', obs: scalp.productBuildup, invert: true },
        { key: 'Redness', obs: scalp.rednessAppearance, invert: true },
        { key: 'Follicles', obs: scalp.follicleVisibility },
        { key: 'Texture', obs: scalp.texture },
      ]
        .map((row) => ({
          ...row,
          label: obsText(row.obs),
          score: healthMetricScore(row.obs, { invert: Boolean(row.invert) }),
        }))
        .filter((row) => row.score != null);

  if (!zoneRows.length && !aggregateMeters.length) return null;

  const notes = (dashboard?.customerSummary?.keyFindings || []).slice(0, 3);
  const insight = String(dashboard?.customerSummary?.overallInsight || '').trim();
  const regionNotes = dashboard?.customerSummary?.regionNotes || {};
  const activeZoneLabel =
    zoneRows.find((z) => z.key === resolvedActiveZone)?.label || resolvedActiveZone;

  return (
    <div className="hi-report__row hi-report__row--scalp">
      <section className="hi-report__card">
        <h3 className="hi-report__section-title">Scalp close-up scores</h3>
        <p className="hi-report__zone-hint hi-report__zone-hint--all">
          {zoneRows.length
            ? 'Per-zone scores from hairline, top, and crown 40× captures.'
            : 'Combined from hairline, top, and crown 40× captures.'}
        </p>
        {zoneRows.length && resolvedActiveZone ? (
          <p className="hi-report__zone-hint hi-report__zone-hint--selected">
            Scores for the selected {activeZoneLabel} close-up.
          </p>
        ) : null}
        {zoneRows.length ? (
          <div
            className="hi-report__scalp-zones"
            data-active-zone={resolvedActiveZone || undefined}
          >
            {zoneRows.map((zone) => (
              <div
                key={zone.key}
                className={`hi-report__scalp-zone${
                  zone.key === resolvedActiveZone ? ' is-active' : ''
                }`}
                data-zone={zone.key}
              >
                <h4>{zone.label}</h4>
                <div className="hi-report__profile-bars hi-report__profile-bars--compact">
                  {zone.metrics.slice(0, 5).map((row) => (
                    <div key={`${zone.key}-${row.key}`} className="hi-report__profile-bar">
                      <div className="hi-report__profile-bar-meta">
                        <strong>{row.key}</strong>
                        <span className={`hi-report__pill is-${statusTone(row.label)}`}>{row.label}</span>
                        <em>{row.score}%</em>
                      </div>
                      <div className="hi-report__bar-track" aria-hidden>
                        <span
                          className={`hi-report__bar-fill is-${barTone(row.score)}`}
                          style={{ width: `${Math.max(4, row.score)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="hi-report__profile-bars">
            {aggregateMeters.map((row) => (
              <div key={row.key} className="hi-report__profile-bar">
                <div className="hi-report__profile-bar-meta">
                  <strong>{row.key}</strong>
                  <span className={`hi-report__pill is-${statusTone(row.label)}`}>{row.label}</span>
                  <em>{row.score}%</em>
                </div>
                <div className="hi-report__bar-track" aria-hidden>
                  <span
                    className={`hi-report__bar-fill is-${barTone(row.score)}`}
                    style={{ width: `${Math.max(4, row.score)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="hi-report__card">
        <h3 className="hi-report__section-title">What it means</h3>
        {insight ? <p className="hi-report__lead-copy">{insight}</p> : null}
        {['hairline', 'top', 'crown'].map((key) => {
          const note = String(regionNotes[key] || '').trim();
          if (!note) return null;
          return (
            <p
              key={key}
              className={`hi-report__zone-note${
                key === resolvedActiveZone ? ' is-active' : ''
              }`}
              data-zone={key}
            >
              {note}
            </p>
          );
        })}
        {notes.length ? (
          <ul className="hi-report__note-list">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

export function HairInsightReportFindings({ dashboard, onFocus }) {
  const summary = dashboard?.customerSummary || {};
  const evidence = (dashboard?.visualEvidence || []).filter((ev) => ev.type !== 'coverage').slice(0, 6);
  const findings =
    evidence.length > 0
      ? evidence
      : (summary.keyFindings || []).slice(0, 4).map((title, i) => ({
          id: `kf-${i}`,
          title,
          observation: '',
          imageUrl: null,
        }));

  return (
    <div className="hi-report__row hi-report__row--findings-visual">
      <section className="hi-report__card">
        <h3 className="hi-report__section-title">
          <IconSpark /> Key Moments
        </h3>
        <div className="hi-report__finding-grid">
          {findings.map((ev) => (
            <button
              key={ev.id || ev.title}
              type="button"
              className="hi-report__finding-card"
              onClick={() => onFocus?.(ev)}
            >
              {ev.imageUrl || ev.microscopeImageUrl ? (
                <img
                  src={resolveInsightAssetUrl(ev.microscopeImageUrl || ev.imageUrl)}
                  alt=""
                />
              ) : (
                <span className="hi-report__finding-ph" />
              )}
              <span className="hi-report__finding-cap">
                <strong>{ev.title}</strong>
                {ev.observation ? (
                  <span className={`hi-report__pill is-${statusTone(ev.observation)}`}>
                    {obsText(ev.observation)}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      </section>
      <div className="hi-report__do-dont-visual">
        <section className="hi-report__card">
          <h3 className="hi-report__section-title hi-report__section-title--do">Do</h3>
          <ul className="hi-report__chip-list is-do">
            {(summary.do || ['Gentle cleanse', 'Light hydration', 'Review with stylist']).slice(0, 4).map((x) => (
              <li key={x}>
                <span className="ico">✓</span>
                {x}
              </li>
            ))}
          </ul>
        </section>
        <section className="hi-report__card">
          <h3 className="hi-report__section-title hi-report__section-title--dont">Don&apos;t</h3>
          <ul className="hi-report__chip-list is-dont">
            {(summary.dont || ['Scratch scalp', 'Harsh chemicals', 'Treat as diagnosis']).slice(0, 4).map((x) => (
              <li key={x}>
                <span className="ico">✕</span>
                {x}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

export function HairInsightReportFooterBands({ dashboard }) {
  return (
    <div className="hi-report__bottom">
      <p className="hi-report__disclaimer">
        Cosmetic guidance only — not a medical diagnosis.
      </p>
    </div>
  );
}
